import React, { useEffect, useMemo, useState } from 'react';
import { Book, BookAppendix } from '../../../../model/response';
import { Button, Drawer, DrawerProps, Input, message } from 'antd';
import { addBookAppendix, updateBookAppendix } from '../../../../service/bookAppendixService';
import processError from '../../../../utils/processError';

interface BookAppendixFormDrawerProps extends Omit<DrawerProps, 'footer'> {
  mode: 'create' | 'update';
  parent?: BookAppendix;
  book?: Book;
  defaultBookAppendix?: BookAppendix;
  onBeforeSuccess: () => Promise<void>;
}

function BookAppendixFormDrawer({
  mode,
  parent,
  book,
  defaultBookAppendix,
  open,
  onBeforeSuccess,
  ...restProps
}: BookAppendixFormDrawerProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  function resetForm() {
    setName('');
    setCode('');
  }

  useEffect(() => {
    if (open) {
      if (defaultBookAppendix) {
        setName(defaultBookAppendix.name);
        setCode(defaultBookAppendix.code);
      } else {
        resetForm();
      }
    }
  }, [open, defaultBookAppendix]);

  const title = useMemo(() => {
    if (mode === 'create') {
      if (parent) {
        return 'Thêm mục lục con';
      }
      return 'Thêm mục lục cấp 1';
    }
    if (parent) {
      return 'Cập nhật mục lục cấp 1';
    }
    return 'Cập nhật mục lục con';
  }, [mode, parent]);

  async function handleSave() {
    if (!book) {
      message.info('Không có thông tin sách');
      return;
    }
    try {
      if (mode === 'create') {
        setLoading(true);
        await addBookAppendix({
          name,
          code,
          book: { id: book.id },
          parent: parent ? { id: parent.id } : undefined,
        });
        await onBeforeSuccess();
        message.info(title + ' thành công');
        setLoading(false);
        return;
      }
      if (!defaultBookAppendix) {
        message.error('Thiếu mã của phụ mục nên không thể cập nhật');
        return;
      }
      setLoading(true);
      await updateBookAppendix(defaultBookAppendix?.id, {
        name,
        code,
      });
      await onBeforeSuccess();
      message.info(title + ' thành công');
      setLoading(false);
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Drawer
      title={title}
      placement="right"
      width={400}
      closable={false}
      open={open}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={(e) => !loading && restProps.onClose && restProps.onClose(e)} className={'mr-2'}>
            Hủy
          </Button>
          <Button type="primary" onClick={handleSave} loading={loading}>
            Lưu
          </Button>
        </div>
      }
      {...restProps}
    >
      <div className={'flex flex-col gap-y-2'}>
        <div>
          <label>
            Tên mục <span className={'text-FF1D30'}>*</span>
          </label>
          <Input placeholder="Nhập tên mục..." value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label>Mã mục</label>
          <Input placeholder="Nhập mã mục..." value={code} onChange={(e) => setCode(e.target.value)} required />
        </div>
      </div>
    </Drawer>
  );
}

export default BookAppendixFormDrawer;
